import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../../App";
import { OPTION_VALUES, OPTION_LABEL_KEYS } from "../../i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-[#fbbf24] outline-none";
const labelCls = "block mono text-xs text-slate-300 mb-1";

const INITIAL = {
  loan_type: OPTION_VALUES.loan_type[0],
  loan_amount: "",
  purpose: "",
  full_name: "",
  age: "",
  phone: "",
  occupation: "",
  salary: "",
  area: "",
  residential_status: OPTION_VALUES.residential_status[0],
  father_working: OPTION_VALUES.father_working[0],
  mother_working: OPTION_VALUES.mother_working[0],
  marital_status: OPTION_VALUES.marital_status[0],
  spouse_occupation: "",
  children: "0",
  siblings: "0",
};

const Field = ({ id, label, children }) => (
  <div>
    <label className={labelCls} htmlFor={id}>{label}</label>
    {children}
  </div>
);

const ThankYou = ({ result }) => {
  const { t } = useLang();
  const [count, setCount] = useState(3);

  useEffect(() => {
    const timer = setInterval(() => setCount((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (count <= 0) {
      window.location.href = result.whatsapp_url;
    }
  }, [count, result.whatsapp_url]);

  return (
    <div className="rounded-2xl border border-[#fbbf24]/40 bg-slate-900/90 p-10 sm:p-14 shadow-2xl text-center" data-testid="thankyou-screen">
      <div className="w-16 h-16 mx-auto rounded-full bg-[#fbbf24]/15 border border-[#fbbf24]/50 flex items-center justify-center mb-6">
        <svg className="w-8 h-8 text-[#fbbf24]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>
      <h3 className="disp text-3xl font-black text-white" data-testid="thankyou-title">
        {t("thank.title")} <span className="text-[#fbbf24]">{t("thank.titleZh")}</span>
      </h3>
      <p className="text-sm text-slate-300 mt-3">{t("thank.sub")}</p>
      <p className="mono text-xs text-slate-400 mt-4">
        {t("thank.ref")}: <span className="text-[#fbbf24] font-bold" data-testid="thankyou-ref">{result.ref}</span>
      </p>
      <p className="mono text-xs text-slate-400 mt-6" data-testid="thankyou-countdown">
        {t("thank.redirect")} ({Math.max(count, 0)}{t("thank.seconds")})…
      </p>
      <p
        className="disp text-lg sm:text-xl font-black text-[#fbbf24] mt-4 leading-snug"
        data-testid="thankyou-press-send"
      >
        {t("thank.press_send")}
      </p>
      <a
        href={result.whatsapp_url}
        data-testid="whatsapp-continue-btn"
        className="inline-block mt-6 disp text-sm font-bold uppercase tracking-wider bg-[#fbbf24] text-[#070d1e] px-8 py-4 rounded hover:bg-[#f59e0b] shadow-lg shadow-[#fbbf24]/10 transition-all"
      >
        {t("thank.cta")}
      </a>
    </div>
  );
};

export const ApplicationForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const topRef = useRef(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const select = (field) => (
    <select
      id={`field-${field}`}
      data-testid={`field-${field}`}
      className={inputCls}
      value={form[field]}
      onChange={set(field)}
    >
      {OPTION_VALUES[field].map((value, i) => (
        <option key={value} value={value}>
          {t(`form.options.${OPTION_LABEL_KEYS[field][i]}`)}
        </option>
      ))}
    </select>
  );

  const textInput = (field, type = "text", extra = {}) => (
    <input
      id={`field-${field}`}
      data-testid={`field-${field}`}
      type={type}
      className={inputCls}
      placeholder={t(`form.placeholders.${field}`) !== `form.placeholders.${field}` ? t(`form.placeholders.${field}`) : ""}
      value={form[field]}
      onChange={set(field)}
      {...extra}
    />
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(String(form.loan_amount).replace(/[^0-9.]/g, ""));
    if (!form.purpose.trim() || !form.full_name.trim() || !form.occupation.trim() || !form.salary.trim() || !form.area.trim()) {
      toast.error(t("form.errors.required"));
      return;
    }
    if (!amount || amount < 1000 || amount > 500000) {
      toast.error(t("form.errors.amount"));
      return;
    }
    const age = parseInt(form.age, 10);
    if (!age || age < 18 || age > 100) {
      toast.error(t("form.errors.required"));
      return;
    }
    if (!/^[0-9+\-\s]{7,20}$/.test(form.phone.trim())) {
      toast.error(t("form.errors.phone"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/applications`, {
        ...form,
        loan_amount: amount,
        age,
        children: parseInt(form.children, 10) || 0,
        siblings: parseInt(form.siblings, 10) || 0,
      });
      setResult(data);
      window.scrollTo({ top: topRef.current?.offsetTop ?? 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("form.errors.submit"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="apply" ref={topRef} className="py-24 border-b border-[#1e293b]/60 bg-[#060b18]" data-testid="apply-section">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="mono text-xs uppercase tracking-widest text-[#fbbf24]">{t("form.eyebrow")}</span>
          <h2 className="disp text-3xl sm:text-4xl font-black text-white mt-2" data-testid="form-title">{t("form.title")}</h2>
          <p className="text-sm text-slate-400 mt-2">{t("form.sub")}</p>
        </div>

        {result ? (
          <ThankYou result={result} />
        ) : (
          <form
            onSubmit={handleSubmit}
            data-testid="application-form"
            className="rounded-2xl border border-slate-700 bg-slate-900/90 p-8 sm:p-10 shadow-2xl space-y-8"
          >
            <div>
              <h3 className="disp text-lg font-bold text-[#fbbf24] border-b border-slate-800 pb-2 mb-4">{t("form.s1")}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="field-loan_type" label={t("form.labels.loan_type")}>{select("loan_type")}</Field>
                <Field id="field-loan_amount" label={t("form.labels.loan_amount")}>{textInput("loan_amount")}</Field>
                <div className="sm:col-span-2">
                  <Field id="field-purpose" label={t("form.labels.purpose")}>{textInput("purpose")}</Field>
                </div>
              </div>
            </div>

            <div>
              <h3 className="disp text-lg font-bold text-[#fbbf24] border-b border-slate-800 pb-2 mb-4">{t("form.s2")}</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field id="field-full_name" label={t("form.labels.full_name")}>{textInput("full_name")}</Field>
                <Field id="field-age" label={t("form.labels.age")}>{textInput("age", "number", { min: 18, max: 100 })}</Field>
                <Field id="field-phone" label={t("form.labels.phone")}>{textInput("phone", "tel")}</Field>
                <Field id="field-occupation" label={t("form.labels.occupation")}>{textInput("occupation")}</Field>
                <Field id="field-salary" label={t("form.labels.salary")}>{textInput("salary")}</Field>
                <Field id="field-area" label={t("form.labels.area")}>{textInput("area")}</Field>
                <div className="sm:col-span-3">
                  <Field id="field-residential_status" label={t("form.labels.residential_status")}>{select("residential_status")}</Field>
                </div>
              </div>
            </div>

            <div>
              <h3 className="disp text-lg font-bold text-[#fbbf24] border-b border-slate-800 pb-2 mb-4">{t("form.s3")}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="field-father_working" label={t("form.labels.father_working")}>{select("father_working")}</Field>
                <Field id="field-mother_working" label={t("form.labels.mother_working")}>{select("mother_working")}</Field>
                <Field id="field-marital_status" label={t("form.labels.marital_status")}>{select("marital_status")}</Field>
                <Field id="field-spouse_occupation" label={t("form.labels.spouse_occupation")}>{textInput("spouse_occupation")}</Field>
                <Field id="field-children" label={t("form.labels.children")}>{textInput("children", "number", { min: 0, placeholder: "0" })}</Field>
                <Field id="field-siblings" label={t("form.labels.siblings")}>{textInput("siblings", "number", { min: 0, placeholder: "0" })}</Field>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-800">
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-application-btn"
                className="w-full disp text-base font-bold uppercase tracking-wider py-4 bg-[#fbbf24] text-[#070d1e] hover:bg-[#f59e0b] rounded-lg shadow-lg shadow-[#fbbf24]/20 transition-all disabled:opacity-60"
              >
                {submitting ? t("form.submitting") : t("form.submit")}
              </button>
              <p className="mono text-[11px] text-center text-slate-400 mt-3">{t("form.note")}</p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
